// apps/roleforge-app/src/components/layout/AuditAppShell.tsx
//
// Outermost full-viewport shell for the audit experience.
//
// Desktop (lg and up) keeps the signature three columns:
//   sidebar (w-64, dark) | converse (flex-1) | assets (w-80)
//
// Issues #1201 / #1235 — those two side columns were 576px of
// non-shrinkable chrome with no responsive rules, so on a phone the chat
// column (the actual product) was squeezed to zero width and the page
// scrolled sideways. Below `lg` the shell is now a single column: the chat
// gets the full viewport, and the steps and Blueprint panels move behind two
// clearly-labelled buttons that open them as overlay drawers.
//
// Uses fixed-position viewport layout so it escapes the parent AppShell's
// max-width container. The audit experience is always full-bleed.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AuditSidebar } from './AuditSidebar';

type Drawer = 'steps' | 'assets';

interface Props {
  currentStep: 1 | 2 | 3 | 4;
  auditId: string;
  workspaceId?: string;
  workspaceName?: string;
  /** The middle column — chat or asset editor. */
  converseColumn: ReactNode;
  /** The right column — asset cards. */
  assetsColumn: ReactNode;
  /** Optional extra sidebar nav items. */
  sidebarActions?: ReactNode;
}

const DRAWER_LABEL: Record<Drawer, string> = {
  steps: 'Your audit steps',
  assets: 'Your Blueprint',
};

export function AuditAppShell({
  currentStep,
  auditId,
  workspaceId,
  workspaceName,
  converseColumn,
  assetsColumn,
  sidebarActions,
}: Props) {
  const [openDrawer, setOpenDrawer] = useState<Drawer | null>(null);
  const closeDrawer = useCallback(() => setOpenDrawer(null), []);

  // Escape closes whichever drawer is open — keyboard users must never be
  // stuck behind an overlay they cannot dismiss.
  useEffect(() => {
    if (!openDrawer) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openDrawer, closeDrawer]);

  const drawerButton = (drawer: Drawer) => (
    <button
      type="button"
      onClick={() => setOpenDrawer(drawer)}
      data-testid={`audit-open-${drawer}`}
      aria-expanded={openDrawer === drawer}
      className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg border border-line px-3 py-2 text-sm font-semibold text-ink transition hover:bg-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
    >
      {DRAWER_LABEL[drawer]}
    </button>
  );

  const closeDrawerButton = (drawer: Drawer) => (
    <button
      type="button"
      onClick={closeDrawer}
      aria-label={`Close ${DRAWER_LABEL[drawer]}`}
      className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-lg px-3 text-sm font-semibold text-ink hover:bg-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
    >
      Close
    </button>
  );

  return (
    <div
      data-testid="audit-app-shell"
      className="fixed inset-0 flex flex-col overflow-x-hidden bg-paper text-ink lg:flex-row"
    >
      {/* Small screens: the two side panels collapse behind these controls. */}
      <div
        data-testid="audit-mobile-panel-bar"
        className="flex shrink-0 items-center gap-2 border-b border-line bg-paper px-3 py-2 lg:hidden"
      >
        {drawerButton('steps')}
        {drawerButton('assets')}
      </div>

      {/* Backdrop for the mobile drawers. */}
      {openDrawer && (
        <button
          type="button"
          aria-label="Close panel"
          onClick={closeDrawer}
          data-testid="audit-drawer-backdrop"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        />
      )}

      <div
        data-testid="audit-sidebar-wrapper"
        className={`shrink-0 lg:static lg:z-auto lg:block ${
          openDrawer === 'steps'
            ? 'fixed inset-y-0 left-0 z-50 max-w-[85vw] overflow-y-auto shadow-2xl'
            : 'hidden'
        }`}
      >
        {openDrawer === 'steps' && (
          <div className="dark-surface flex justify-end px-2 pt-2 lg:hidden">
            {closeDrawerButton('steps')}
          </div>
        )}
        <AuditSidebar
          currentStep={currentStep}
          auditId={auditId}
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          extraActions={sidebarActions}
        />
      </div>

      <main
        data-testid="audit-converse-column"
        className="flex min-h-0 min-w-0 flex-1 flex-col border-t-2 border-coral"
      >
        {converseColumn}
      </main>

      <div
        data-testid="audit-assets-column-wrapper"
        className={`border-coral lg:static lg:z-auto lg:block lg:w-80 lg:shrink-0 lg:border-t-2 ${
          openDrawer === 'assets'
            ? 'fixed inset-y-0 right-0 z-50 w-80 max-w-[85vw] overflow-y-auto bg-paper shadow-2xl'
            : 'hidden'
        }`}
      >
        {openDrawer === 'assets' && (
          <div className="flex justify-start bg-paper px-2 pt-2 lg:hidden">
            {closeDrawerButton('assets')}
          </div>
        )}
        {assetsColumn}
      </div>
    </div>
  );
}
